import { c as _c } from "react/compiler-runtime";
import { revealOnHoverClassName, touchTargetClassName } from "@/components/ui/control-surface";
import { Spinner } from "@/components/ui/Spinner";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/Tooltip";
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import type { VariantProps } from "class-variance-authority";
import * as React from "react";
import cn from "twsx";

// Linear button: a pill with a 1px transparent border so states never shift
// layout. Raised variants draw their edge with a ring shadow. Hover snaps in
// and eases out over 150ms. sm 24 · md 28 (default) · lg 32; `inline` sits in
// running text with no box. `media` variants sit on photos and camera views.
// `segment` variants and size fill one segment of a FilterChip. `iconOnlyBelowMd`
// collapses a labelled button to a centred square icon on narrow screens.
const ring = (alpha: number) => `[box-shadow:lch(0_0_0/${alpha})_0px_0px_0px_1px,var(--elevation-low)]`;
const buttonVariants = cva(["relative m-0 inline-flex max-w-full shrink-0 cursor-pointer select-none items-center justify-center whitespace-nowrap", "rounded-full border border-transparent font-medium outline-none transition duration-150 hover:duration-0", "focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-accent", "disabled:pointer-events-none disabled:opacity-disabled aria-disabled:opacity-disabled", "[&_svg]:pointer-events-none [&_svg]:shrink-0"], {
  variants: {
    variant: {
      primary: "bg-accent text-text-on-accent [box-shadow:var(--elevation-low)] hover:bg-accent-hover active:bg-accent-pressed",
      secondary: cn("bg-bg-surface text-text-primary hover:bg-fill-pill-hover", ring(0.103), "hover:[box-shadow:lch(0_0_0/0.147)_0px_0px_0px_1px,var(--elevation-low)]"),
      tertiary: cn("bg-bg-control text-text-tertiary hover:bg-fill-tab-hover hover:text-text-secondary", ring(0.105), "hover:[box-shadow:lch(0_0_0/0.15)_0px_0px_0px_1px,var(--elevation-low)]"),
      ghost: "bg-transparent text-text-secondary hover:bg-fill-hover-subtle hover:text-text-primary",
      danger: "bg-red-light text-white [box-shadow:var(--elevation-low)] hover:bg-red-hover",
      warning: "bg-yellow-light text-gray-900 [box-shadow:var(--elevation-low)] hover:bg-yellow-hover",
      link: "bg-transparent text-text-link hover:bg-fill-hover-subtle hover:text-text-primary",
      dangerGhost: "bg-transparent text-red-text hover:bg-red-bg-subtle",
      media: "bg-white/10 text-white [box-shadow:inset_0_0_0_1px_rgb(255_255_255/0.3)] hover:bg-white/20",
      mediaGhost: "bg-transparent text-white/75 hover:bg-white/10 hover:text-white",
      segment: cn("rounded-none border-0 bg-transparent font-normal text-text-tertiary", "hover:bg-fill-pill-hover focus-visible:-outline-offset-2 data-[state=open]:bg-fill-pill-hover"),
      segmentValue: cn("min-w-0 shrink rounded-none border-0 bg-transparent font-normal text-text-primary", "hover:bg-fill-pill-hover focus-visible:-outline-offset-2 data-[state=open]:bg-fill-pill-hover")
    },
    size: {
      sm: "h-6 gap-1 px-2 text-sm has-[>svg:first-child]:pl-1.5 [&_svg]:size-3",
      md: "h-7 gap-1.5 px-2.5 text-sm has-[>svg:first-child]:pl-2 [&_svg]:size-3.5",
      lg: "h-8 gap-1.5 px-3 text-base has-[>svg:first-child]:pl-2.5 [&_svg]:size-3.5",
      inline: "h-auto gap-1 p-0 [font-size:inherit] [font-weight:inherit] hover:bg-transparent hover:underline [&_svg]:size-3.5",
      segment: "h-full gap-1 px-1.75 text-sm"
    },
    iconOnlyBelowMd: {
      true: cn("max-md:aspect-square max-md:gap-0 max-md:px-0", "max-md:has-[>svg:first-child]:pl-0 max-md:[&>:not(svg)]:hidden"),
      false: ""
    },
    reveal: {
      true: revealOnHoverClassName,
      false: ""
    }
  },
  defaultVariants: {
    variant: "primary",
    size: "md",
    reveal: false,
    iconOnlyBelowMd: false
  }
});
export type ButtonProps = {
  asChild?: boolean;
  isLoading?: boolean;
  tooltip?: string;
  ref?: React.Ref<HTMLButtonElement>;
} & React.ButtonHTMLAttributes<HTMLButtonElement> & VariantProps<typeof buttonVariants>;

/** Use when the person triggers an action with a text label. */
function Button(t0) {
  const $ = _c(39);
  let children;
  let className;
  let disabled;
  let iconOnlyBelowMd;
  let props;
  let ref;
  let reveal;
  let size;
  let t1;
  let t2;
  let tooltip;
  let variant;
  if ($[0] !== t0) {
    ({
      className,
      variant,
      size,
      reveal,
      iconOnlyBelowMd,
      asChild: t1,
      isLoading: t2,
      children,
      tooltip,
      disabled,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = disabled;
    $[4] = iconOnlyBelowMd;
    $[5] = props;
    $[6] = ref;
    $[7] = reveal;
    $[8] = size;
    $[9] = t1;
    $[10] = t2;
    $[11] = tooltip;
    $[12] = variant;
  } else {
    children = $[1];
    className = $[2];
    disabled = $[3];
    iconOnlyBelowMd = $[4];
    props = $[5];
    ref = $[6];
    reveal = $[7];
    size = $[8];
    t1 = $[9];
    t2 = $[10];
    tooltip = $[11];
    variant = $[12];
  }
  const asChild = t1 === undefined ? false : t1;
  const isLoading = t2 === undefined ? false : t2;
  const Comp = asChild ? Slot : "button";
  let t3;
  if ($[13] !== className || $[14] !== iconOnlyBelowMd || $[15] !== isLoading || $[16] !== reveal || $[17] !== size || $[18] !== variant) {
    t3 = cn(buttonVariants({
      variant,
      size,
      reveal,
      iconOnlyBelowMd
    }), touchTargetClassName, isLoading && "pointer-events-none", className);
    $[13] = className;
    $[14] = iconOnlyBelowMd;
    $[15] = isLoading;
    $[16] = reveal;
    $[17] = size;
    $[18] = variant;
    $[19] = t3;
  } else {
    t3 = $[19];
  }
  const t4 = disabled || isLoading;
  const t5 = isLoading || undefined;
  let t6;
  if ($[20] !== asChild || $[21] !== children || $[22] !== isLoading) {
    t6 = isLoading && !asChild ? <><span className="contents invisible">{children}</span><Spinner size="sm" className="absolute inset-0 m-auto" /></> : children;
    $[20] = asChild;
    $[21] = children;
    $[22] = isLoading;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  let t7;
  if ($[24] !== Comp || $[25] !== props || $[26] !== ref || $[27] !== t3 || $[28] !== t4 || $[29] !== t5 || $[30] !== t6) {
    t7 = <Comp className={t3} disabled={t4} aria-busy={t5} ref={ref} {...props}>{t6}</Comp>;
    $[24] = Comp;
    $[25] = props;
    $[26] = ref;
    $[27] = t3;
    $[28] = t4;
    $[29] = t5;
    $[30] = t6;
    $[31] = t7;
  } else {
    t7 = $[31];
  }
  const button = t7;
  if (!tooltip) {
    return button;
  }
  let t8;
  if ($[32] !== button) {
    t8 = <TooltipTrigger asChild={true}>{button}</TooltipTrigger>;
    $[32] = button;
    $[33] = t8;
  } else {
    t8 = $[33];
  }
  let t9;
  if ($[34] !== tooltip) {
    t9 = <TooltipContent>{tooltip}</TooltipContent>;
    $[34] = tooltip;
    $[35] = t9;
  } else {
    t9 = $[35];
  }
  let t10;
  if ($[36] !== t8 || $[37] !== t9) {
    t10 = <Tooltip>{t8}{t9}</Tooltip>;
    $[36] = t8;
    $[37] = t9;
    $[38] = t10;
  } else {
    t10 = $[38];
  }
  return t10;
}
export { Button, buttonVariants };